import { createContext, useRef, useState, type HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import type { FolderOption } from '@utils/folders/folder-tree';
import type { WorkspaceRow } from './workspace';
import { useWorkspaceDragPreview } from './use-workspace-drag-preview';
import { useWorkspaceShiftDrag } from './use-workspace-shift-drag';
import {
  canMoveRows,
  getDraggedRows,
  WORKSPACE_DRAG_TYPE
} from './workspace-dnd-rules';

export type DropTargetProps = HTMLAttributes<HTMLElement> & {
  'data-drop-active'?: boolean;
  'data-drop-hint'?: string;
  'data-workspace-drop-target'?: string;
};
type GetDropTargetProps = (
  id: number | null,
  name: string,
  acceptsFiles?: boolean
) => DropTargetProps;
export const WorkspaceDropContext = createContext<GetDropTargetProps | null>(
  null
);

export function useWorkspaceDnd({
  currentFolderId,
  folders,
  moveRows
}: {
  currentFolderId: number | null;
  folders: FolderOption[] | undefined;
  moveRows: (rows: WorkspaceRow[], targetId: number | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const draggedRows = useRef<WorkspaceRow[]>([]);
  const dragPreview = useWorkspaceDragPreview();
  const moving = useRef(false);
  const [isMoving, setIsMoving] = useState(false);
  const [activeTarget, setActiveTarget] = useState<number | null | undefined>();
  const [hint, setHint] = useState('');

  const reset = () => {
    draggedRows.current = [];
    dragPreview.clear();
    setActiveTarget(undefined);
    setHint('');
  };
  const shiftDrag = useWorkspaceShiftDrag(draggedRows, reset, dragPreview);

  const getDropTargetProps: GetDropTargetProps = (
    id,
    name,
    acceptsFiles = false
  ) => {
    const active = activeTarget === id;
    const accepted = (event: React.DragEvent<HTMLElement>) => {
      const types = Array.from(event.dataTransfer.types);
      if (moving.current) return false;
      if (types.includes(WORKSPACE_DRAG_TYPE)) {
        return canMoveRows(draggedRows.current, id, currentFolderId, folders);
      }
      return (
        acceptsFiles &&
        types.includes('Files') &&
        !document.querySelector('[data-uppy-theme]')
      );
    };
    return {
      'data-workspace-drop-target': id === null ? 'home' : String(id),
      'data-drop-active': active,
      'data-drop-hint': active ? hint : undefined,
      title: active ? hint : undefined,
      style: active
        ? {
            backgroundColor: getBrandTint(0.16),
            outline: `2px solid ${getColour('activeText')}`,
            outlineOffset: '-2px'
          }
        : undefined,
      onDragOver: event => {
        if (!accepted(event)) {
          if (
            Array.from(event.dataTransfer.types).includes(WORKSPACE_DRAG_TYPE)
          ) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'none';
          }
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = Array.from(
          event.dataTransfer.types
        ).includes('Files')
          ? 'copy'
          : 'move';
        setActiveTarget(id);
        setHint(
          t(
            Array.from(event.dataTransfer.types).includes('Files')
              ? 'upload-to-folder'
              : 'move-to-folder',
            { name }
          )
        );
      },
      onDragLeave: event => {
        if (
          event.relatedTarget instanceof Node &&
          event.currentTarget.contains(event.relatedTarget)
        )
          return;
        setActiveTarget(undefined);
        setHint('');
      },
      onDrop: event => {
        const internal = Array.from(event.dataTransfer.types).includes(
          WORKSPACE_DRAG_TYPE
        );
        const valid = accepted(event);
        const rows = draggedRows.current;
        reset();
        // Computer-file drops bubble to useDragToUpload, which owns the modal.
        if (!internal) return;
        event.preventDefault();
        event.stopPropagation();
        if (!valid) return;
        moving.current = true;
        setIsMoving(true);
        void moveRows(rows, id).finally(() => {
          moving.current = false;
          setIsMoving(false);
        });
      }
    };
  };

  const getRowProps = (
    row: WorkspaceRow,
    selection: WorkspaceRow[]
  ): HTMLAttributes<HTMLTableRowElement> & {
    'data-upload-folder-id'?: number;
    'data-dragging'?: boolean;
  } => {
    const targetProps: HTMLAttributes<HTMLTableRowElement> & {
      'data-upload-folder-id'?: number;
    } =
      row.variant === 'folder'
        ? {
            ...getDropTargetProps(Number(row.id), row.name ?? '', true),
            'data-upload-folder-id': Number(row.id)
          }
        : {};
    return {
      ...targetProps,
      'data-dragging': dragPreview.isDragging(row),
      style: {
        ...targetProps.style,
        opacity: dragPreview.isDragging(row) ? 0.4 : undefined
      },
      draggable: !isMoving,
      onMouseDown: event => {
        if (!moving.current) shiftDrag.onMouseDown(event, row, selection);
      },
      onDragStart: event => {
        shiftDrag.cancel();
        if (
          (event.target as HTMLElement).closest(
            'button, input, select, textarea, [role="button"]'
          )
        ) {
          event.preventDefault();
          return;
        }
        draggedRows.current = getDraggedRows(row, selection);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(WORKSPACE_DRAG_TYPE, 'move');
        dragPreview.start(draggedRows.current, event, event.dataTransfer);
      },
      onDragEnd: reset
    };
  };

  return {
    getRowProps,
    getDropTargetProps,
    isMoving,
    hint,
    preview: dragPreview.preview
  };
}
